import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import variables from 'styles/vendor/bootstrap/variables'
import Places from 'constants/places'
import FilterChip from 'LuxKit/Chips/FilterChip'
import qs from 'qs'
import {
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import useTourFacetLocationCategory from 'hooks/TourV2/useTourFacetLocationCategory'
import useOfferList from 'hooks/Offers/useOfferList'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import Group from 'components/utils/Group'
import { OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import { EmptyArray, take } from 'lib/array/arrayUtils'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import getOfferListKey from 'lib/offer/offerListKey'
import BodyText from 'LuxKit/Typography/BodyText'

const layoutContainerPadding = variables.gutterWidth / 2

const FilterChips = styled.div`
  display: flex;
  grid-row: 2;
  gap: ${rem(4)};
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  /*
    negative margin left/right lets us 'overflow' the layout container to show more items are scrollable
    negiatve margin + padding top/bottom give pixel calculations space to not accidently hide chip borders
  */
  padding: ${rem(4)} ${rem(layoutContainerPadding)} ${rem(4)} ${rem(layoutContainerPadding)};
  margin: -${rem(4)} -${rem(layoutContainerPadding)} -${rem(4)} -${rem(layoutContainerPadding)};

  &::-webkit-scrollbar {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    padding: 0;
    margin: 0;
    scrollbar-width: auto;

    &::-webkit-scrollbar {
      display: initial;
    }
  }
`

interface Props {
  offerIdsToExclude?: Array<string>
  places?: Array<App.Place>
  title: React.ReactNode
  subtitle?: string
  category?: string
  style?: string
  sortType?: App.OfferListSortByOptions
  flashOffer?: boolean
  onSale?: boolean
  /** Uses to return offer ids display to avoid duplications */
  onOffersShow?: (offerIds: Array<string>) => void
}

function TourPageSearchCarousel(props: Props) {
  const { places, category, style, sortType, title, subtitle, flashOffer, onSale, offerIdsToExclude, onOffersShow } = props
  const intl = useIntl()
  const [selectedPlace, setSelectedPlace] = useState(Places.Anywhere)

  const viewAllText = intl.formatMessage(
    {
      id: 'hEePn3bTkMPD76atV6nf',
      defaultMessage: 'View all',
      description: 'Button text to view all tour offers in the carousel category',
    },
  )
  const aroundTheWorldText = intl.formatMessage(
    {
      id: 'VgKoAHky6qrKzSjbLtRa',
      defaultMessage: 'Around the world',
      description: 'Filter chip label for showing tours from all destinations worldwide',
    },
  )

  const excludedIds = useMemo(() => {
    if (selectedPlace.id === Places.Anywhere.id) {
      return offerIdsToExclude
    }
    return EmptyArray
  }, [selectedPlace, offerIdsToExclude])

  const tourFacets = useTourFacetLocationCategory({ category, places, flashOffer, style, onSale })

  const carouselFilters: App.OfferListFilters = useMemo(() => ({
    destinationId: selectedPlace.id,
    destinationName: selectedPlace.name.split(',', 1)[0],
    offerTypes: [OFFER_TYPE_TOUR_V2],
    flashOffer: flashOffer ? true : undefined,
    categories: category ? [category] : undefined,
    tourStyles: style ? [style] : undefined,
    onSale: onSale ? true : undefined,
    sortBy: sortType,
    limit: 8,
  }), [selectedPlace, category, sortType, flashOffer, style, onSale])

  const searchUrl = useMemo(() => {
    const { sortBy, limit, offerTypes, ...restFilters } = carouselFilters
    const queryUrl = qs.stringify(restFilters, { arrayFormat: 'comma' })
    return `/search/tours?${queryUrl}`
  }, [carouselFilters])

  const locations = useMemo(() => {
    let locationsToDisplay: Array<any> = []
    if (places === undefined) {
      locationsToDisplay = Array.from(tourFacets.locations.values())
    } else if (places.length > 0) {
      locationsToDisplay = places.filter(place => tourFacets.locations.has(place.id))
    }
    return take([Places.Anywhere, ...locationsToDisplay], 5)
  }, [places, tourFacets.locations])

  const trackFilterName = useMemo(() => {
    if (onSale)
    { return 'Limited Time Specials' }
    if (flashOffer)
    { return 'Limited Time Offer' }
    if (category)
    { return category }
    if (style)
    { return style }
  }, [flashOffer, category, style, onSale])

  const tourIds = useOfferList(carouselFilters, { excludedIds })
  const tracking = useOfferListTracking(getListName('OfferList', 'TOURS', `${trackFilterName} Carousel`), { key: carouselFilters ? getOfferListKey(carouselFilters) : undefined })

  const hasInitToursLoaded = useRef(false)
  useEffect(() => {
    if (!hasInitToursLoaded.current && !tourIds.fetching && !!tourIds.offerIds.length) {
      hasInitToursLoaded.current = true
      onOffersShow?.(take(tourIds.offerIds, 3))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourIds])

  if (!tourIds.fetching && tourIds.offerIds.length === 0) {
    return null
  }

  return (
    <Group direction="vertical" gap={20}>
      <LayoutContainer>
        <Group direction="vertical" gap={20}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
            <div>
              <Heading variant="heading2">{title}</Heading>
              {subtitle && <BodyText variant="large">{subtitle}</BodyText>}
            </div>
            <TextButton
              kind="tertiary"
              to={searchUrl}
            >
              {viewAllText}
            </TextButton>
          </Group>
          {locations.length > 1 && <FilterChips>
            {locations.map(place => <FilterChip
              key={place.id}
              size="medium"
              selected={selectedPlace.id === place.id}
              onClick={() => setSelectedPlace(place)}
            >
              {place === Places.Anywhere ? aroundTheWorldText : place.name}
            </FilterChip>,
            )}
          </FilterChips>}
        </Group>
      </LayoutContainer>
      <OfferListEventsProvider tracking={tracking}>
        <OfferListCarousel
          tileStyle="card-default"
          tileSize="medium"
          overrideOfferList={tourIds}
        />
      </OfferListEventsProvider>

    </Group>
  )
}

export default TourPageSearchCarousel
